import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  ArrowLeft,
  Save,
  Plus,
  Minus,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import useAuth from '../../hooks/useAuth';
import { getActiveMagasins, getMagasinsByManager, getAllMagasins } from "../../api/magasins";
import { getStocksByMagasin } from "../../api/stock";
import {
  getOrdersByStatus,
  getOrderById,
  createDeliveryNote,
  getDeliveryNotesByOrder,
} from "../../api/orders";

const CreateDeliveryNote = ({ onCancel, onSuccess, preselectedOrderId }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [stockErrors, setStockErrors] = useState([]); // Nouveau état pour les erreurs de stock
  const [stockWarnings, setStockWarnings] = useState([]); // Nouveau état pour les avertissements de stock
  const [magasins, setMagasins] = useState([]);
  const [selectedMagasinId, setSelectedMagasinId] = useState("");
  const [magasinStockMap, setMagasinStockMap] = useState({}); // { productId: quantité }
  const [magasinLocked, setMagasinLocked] = useState(false);
  const { user, isAdmin } = useAuth();

  const [creationDate, setCreationDate] = useState(new Date().toISOString().split("T")[0]);
  const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().split("T")[0]);


  // Charger les commandes qui peuvent être livrées (statut confirmé)
  useEffect(() => {
    const fetchMagasins = async () => {
        try {
            const data = isAdmin
                ? await getActiveMagasins()
                : await getMagasinsByManager(user.id);     // gestionnaire : ses magasins
            // ne garder que les actifs
            const list = (Array.isArray(data) ? data : []).filter(m => m.active !== false);
            setMagasins(list);
        } catch (err) {
            console.error('Erreur chargement magasins:', err);
            setMagasins([]);
        }
    };
    if (user) fetchMagasins();
}, [user, isAdmin]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setOrderLoading(true);
        // Récupérer les commandes avec statut CONFIRMED
        const response = await getOrdersByStatus("CONFIRMED");
        setOrders(response);
      } catch (err) {
        setError("Erreur lors du chargement des commandes");
        console.error(err);
      } finally {
        setOrderLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Pré-sélection de la commande quand on arrive depuis OrderDetail.
  // Si la commande n'est pas dans la liste CONFIRMED, on la récupère par son id
  // et on l'ajoute : l'effet se redéclenche alors et la sélectionne.
  useEffect(() => {
    const preselect = async () => {
      if (!preselectedOrderId || selectedOrder) return;

      const inList = orders.find((o) => o.id === parseInt(preselectedOrderId));
      if (inList) {
        handleOrderSelect({ target: { value: String(preselectedOrderId) } });
      } else if (orders.length > 0) {
        try {
          const fetched = await getOrderById(preselectedOrderId);
          if (fetched) {
            setOrders((prev) =>
              prev.some((o) => o.id === fetched.id) ? prev : [fetched, ...prev]
            );
          }
        } catch (err) {
          console.error("Commande pré-sélectionnée introuvable:", err);
        }
      }
    };
    preselect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedOrderId, orders, selectedOrder]);

  console.log("orders recup ", orders);

  // Fonction pour vérifier le stock disponible
  const checkStockAvailability = (
    items,
    stockMap = magasinStockMap,
    orderType = selectedOrder?.type,
    magasinId = selectedMagasinId,
  ) => {
    if (orderType !== "CLIENT") {
      setStockErrors([]);
      setStockWarnings([]);
      return true;
    }
    if (!magasinId) {
      setStockErrors([
        { index: -1, message: "Veuillez sélectionner un magasin source." },
      ]);
      setStockWarnings([]);
      return false;
    }
    const errors = [];
    const warnings = [];
    items.forEach((item, index) => {
      const stockQuantity = stockMap[item.productId] ?? 0;
      const requestedQuantity = item.deliveredQuantity;
      if (requestedQuantity > 0 && stockQuantity === 0) {
        errors.push({
          index,
          message: `${item.productName} (${item.productCode}) : rupture dans ce magasin`,
        });
      } else if (requestedQuantity > stockQuantity) {
        errors.push({
          index,
          message: `${item.productName} (${item.productCode}) : stock insuffisant (${stockQuantity} dispo, ${requestedQuantity} demandé)`,
        });
      } else if (stockQuantity < 5 && requestedQuantity > 0) {
        warnings.push({
          index,
          message: `${item.productName} (${item.productCode}) : stock faible (${stockQuantity} restant)`,
        });
      }
    });
    setStockErrors(errors);
    setStockWarnings(warnings);
    return errors.length === 0;
  };

  const loadMagasinStock = async (magasinId) => {
    try {
      const stocks = await getStocksByMagasin(magasinId);
      const map = {};
      (Array.isArray(stocks) ? stocks : []).forEach((s) => {
        map[s.productId] = s.quantity;
      });
      setMagasinStockMap(map);
      return map;
    } catch (err) {
      console.error("Erreur chargement stock magasin:", err);
      setMagasinStockMap({});
      return {};
    }
  };

  const handleMagasinSelect = async (e) => {
    const magasinId = e.target.value;
    setSelectedMagasinId(magasinId);
    if (!magasinId) {
      setMagasinStockMap({});
      return;
    }
    const map = await loadMagasinStock(magasinId);
    checkStockAvailability(deliveryItems, map, selectedOrder?.type, magasinId);
  };

  const handleOrderSelect = async (e) => {
    const orderId = e.target.value;
    if (!orderId) {
      setSelectedOrder(null);
      setOrderItems([]);
      setDeliveryItems([]);
      setStockErrors([]);
      setStockWarnings([]);
      setSelectedMagasinId("");
      setMagasinLocked(false);
      setMagasinStockMap({});
      return;
    }

    const selected = orders.find((order) => order.id === parseInt(orderId));
    if (!selected) return;
    setSelectedOrder(selected);
    setOrderItems(selected.items || []);

    // BL existants → cumul déjà livré + magasin imposé
    let existingNotes = [];
    try {
      existingNotes = await getDeliveryNotesByOrder(orderId);
      if (!Array.isArray(existingNotes)) existingNotes = [];
    } catch (err) {
      console.error(err);
      existingNotes = [];
    }

    const deliveredByProduct = {};
    existingNotes.forEach((note) => {
      (note.items || []).forEach((it) => {
        deliveredByProduct[it.productId] =
          (deliveredByProduct[it.productId] || 0) + (it.deliveredQuantity || 0);
      });
    });

    let lockedMagasinId = "";
    let effectiveMagasinId = "";
    let stockMap = {};
    if (existingNotes.length > 0 && existingNotes[0].magasinId) {
      // Magasin imposé par une livraison déjà existante
      lockedMagasinId = String(existingNotes[0].magasinId);
      effectiveMagasinId = lockedMagasinId;
      setSelectedMagasinId(lockedMagasinId);
      setMagasinLocked(true);
      stockMap = await loadMagasinStock(lockedMagasinId);
    } else if (magasins.length === 1) {
      // Un seul magasin disponible (cas du gestionnaire mono-magasin)
      // → sélection automatique dès le choix de la commande
      effectiveMagasinId = String(magasins[0].id);
      setSelectedMagasinId(effectiveMagasinId);
      setMagasinLocked(false);
      stockMap = await loadMagasinStock(effectiveMagasinId);
    } else {
      setSelectedMagasinId("");
      setMagasinLocked(false);
      setMagasinStockMap({});
    }

    const deliveryItemsData = (selected.items || []).map((item) => {
      const already = deliveredByProduct[item.product.id] || 0;
      const remaining = Math.max(0, item.quantity - already);
      return {
        productId: item.product.id,
        productName: item.product.name,
        productCode: item.product.sku,
        productUnit: item.product.unit,
        product: item.product,
        quantity: item.quantity,
        alreadyDelivered: already,
        remaining: remaining,
        deliveredQuantity: remaining, // par défaut : livrer le reste
        notes: "",
      };
    });

    setDeliveryItems(deliveryItemsData);
    checkStockAvailability(
      deliveryItemsData,
      stockMap,
      selected.type,
      effectiveMagasinId,
    );
  };

  // Mettre à jour la quantité livrée pour un produit
  const handleQuantityChange = (index, value) => {
    const updatedItems = [...deliveryItems];
    const remaining = updatedItems[index].remaining;
    let newValue = parseInt(value);
    if (isNaN(newValue) || newValue < 0) newValue = 0;
    else if (newValue > remaining) newValue = remaining;
    updatedItems[index].deliveredQuantity = newValue;
    setDeliveryItems(updatedItems);
    checkStockAvailability(updatedItems);
  };

  // Mettre à jour les notes pour un produit
  const handleItemNoteChange = (index, value) => {
    const updatedItems = [...deliveryItems];
    updatedItems[index].notes = value;
    setDeliveryItems(updatedItems);
  };

  // Soumettre le bon de livraison
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Vérifier le stock avant soumission
    const stockCheck = checkStockAvailability(deliveryItems);
    if (!stockCheck) {
      setError(
        "Impossible de créer le bon de livraison : problèmes de stock détectés",
      );
      return;
    }

    try {
      setLoading(true);
      setError("");

      const deliveryNoteData = {
        orderId: selectedOrder.id,
        magasinId: selectedMagasinId ? Number(selectedMagasinId) : null, // ✅ ajouté
        deliveryDate: deliveryDate ? `${deliveryDate}T00:00:00` : new Date().toISOString(),
        createdAt: creationDate ? `${creationDate}T00:00:00` : null,
        notes: notes,
        items: deliveryItems
          .filter((item) => item.deliveredQuantity > 0)
          .map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
            deliveredQuantity: item.deliveredQuantity,
            notes: item.notes,
          })),
      };

      const response = await createDeliveryNote(deliveryNoteData);

      setSuccess(true);

      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);
    } catch (err) {
      setError("Erreur lors de la création du bon de livraison");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Incrémenter/décrémenter les quantités
  const incrementQuantity = (index) => {
    const updatedItems = [...deliveryItems];
    const remaining = updatedItems[index].remaining;
    const stockQuantity =
      magasinStockMap[updatedItems[index].productId] ?? Infinity;
    const maxAllowed =
      selectedOrder?.type === "CLIENT"
        ? Math.min(remaining, stockQuantity)
        : remaining;
    if (updatedItems[index].deliveredQuantity < maxAllowed) {
      updatedItems[index].deliveredQuantity += 1;
      setDeliveryItems(updatedItems);
      checkStockAvailability(updatedItems);
    }
  };

  const decrementQuantity = (index) => {
    const updatedItems = [...deliveryItems];
    if (updatedItems[index].deliveredQuantity > 0) {
      updatedItems[index].deliveredQuantity -= 1;
      setDeliveryItems(updatedItems);
      checkStockAvailability(updatedItems);
    }
  };

  // Supprimer un produit du bon de livraison
  const removeItem = (index) => {
    const updatedItems = [...deliveryItems];
    updatedItems[index].deliveredQuantity = 0;
    setDeliveryItems(updatedItems);
    checkStockAvailability(updatedItems);
  };

  // Fonction utilitaire pour obtenir la classe CSS selon l'état du stock
  const getRowClass = (index) => {
    const hasError = stockErrors.some((error) => error.index === index);
    const hasWarning = stockWarnings.some((warning) => warning.index === index);

    if (hasError) return "table-danger";
    if (hasWarning) return "table-warning";
    return "";
  };

  // Stock du produit dans le magasin sélectionné (null si aucun magasin choisi)
  const getItemStock = (item) =>
    selectedMagasinId ? (magasinStockMap[item.productId] ?? 0) : null;

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && (
        <Alert variant="success">
          Bon de livraison créé avec succès! Redirection en cours...
        </Alert>
      )}

      {/* Affichage des erreurs de stock */}
      {stockErrors.length > 0 && (
        <Alert variant="danger">
          <div className="d-flex align-items-center mb-2">
            <AlertTriangle size={20} className="me-2" />
            <strong>Erreurs de stock détectées :</strong>
          </div>
          <ul className="mb-0">
            {stockErrors.map((error, index) => (
              <li key={index}>{error.message}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Affichage des avertissements de stock */}
      {stockWarnings.length > 0 && (
        <Alert variant="warning">
          <div className="d-flex align-items-center mb-2">
            <AlertTriangle size={20} className="me-2" />
            <strong>Avertissements de stock :</strong>
          </div>
          <ul className="mb-0">
            {stockWarnings.map((warning, index) => (
              <li key={index}>{warning.message}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Card>
        <Card.Header>
          <h5 className="mb-0">Informations du bon de livraison</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Commande à livrer</Form.Label>
                  <Form.Select
                    value={selectedOrder ? selectedOrder.id : ""}
                    onChange={handleOrderSelect}
                    disabled={orderLoading || loading}
                    required
                  >
                    <option value="">Sélectionner une commande</option>
                    {Array.isArray(orders) &&
                      orders.map((order) => (
                        <option key={order.id} value={order.id}>
                          {order.orderNumber} - {order.partner?.name}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Magasin{" "}
                      {selectedOrder?.type === "CLIENT"
                        ? "source"
                        : "destinataire"}{" "}
                      *
                    </Form.Label>
                    <Form.Select
                      value={selectedMagasinId}
                      onChange={handleMagasinSelect}
                      required
                      disabled={magasinLocked}
                    >
                      <option value="">Sélectionner un magasin</option>
                      {magasins.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} ({m.code})
                        </option>
                      ))}
                    </Form.Select>
                    {magasinLocked && (
                      <Form.Text className="text-muted">
                        Magasin imposé par une livraison déjà existante.
                      </Form.Text>
                    )}
                  </Form.Group>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Date de création</Form.Label>
                  <Form.Control
                    type="date"
                    value={creationDate}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setCreationDate(e.target.value)}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Date de livraison</Form.Label>
                  <Form.Control
                    type="date"
                    value={deliveryDate}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Notes générales</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Informations supplémentaires sur cette livraison..."
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
            </Row>

            {orderLoading && selectedOrder && (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p>Chargement des détails de la commande...</p>
              </div>
            )}

            {selectedOrder && !orderLoading && deliveryItems.length > 0 && (
              <>
                <h5 className="mb-3">Produits à livrer</h5>
                <Table bordered responsive>
                  <thead>
                    <tr>
                      <th>Référence</th>
                      <th>Produit</th>
                      <th>Stock disponible</th>
                      <th>Quantité commandée</th>
                      <th>Quantité à livrer</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveryItems.map((item, index) => (
                      <tr key={index} className={getRowClass(index)}>
                        <td>{item.productCode}</td>
                        <td>{item.productName}</td>
                        <td className="text-center">
                          {(() => {
                            const stock = getItemStock(item);
                            if (stock === null)
                              return <span className="text-muted">—</span>;
                            const cls =
                              stock === 0
                                ? "bg-danger"
                                : stock < 5
                                  ? "bg-warning"
                                  : "bg-success";
                            return (
                              <span className={`badge ${cls}`}>
                                {stock} {item.productUnit || "unités"}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="text-center">
                          {item.quantity} {item.productUnit || "unités"}
                        </td>
                        <td style={{ width: "200px" }}>
                          <div className="d-flex align-items-center">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => decrementQuantity(index)}
                              disabled={loading || item.deliveredQuantity <= 0}
                            >
                              <Minus size={16} />
                            </Button>
                            <Form.Control
                              type="number"
                              onWheel={(e) => e.target.blur()}
                              value={item.deliveredQuantity}
                              onChange={(e) =>
                                handleQuantityChange(index, e.target.value)
                              }
                              min="0"
                              max={
                                selectedOrder?.type === "CLIENT"
                                  ? Math.min(
                                      item.remaining,
                                      magasinStockMap[item.productId] ?? 0,
                                    )
                                  : item.quantity
                              }
                              className="mx-2 text-center"
                              disabled={loading}
                            />
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => incrementQuantity(index)}
                              disabled={
                                loading ||
                                item.deliveredQuantity >= item.remaining ||
                                (selectedOrder?.type === "CLIENT" &&
                                  item.deliveredQuantity >=
                                    (magasinStockMap[item.productId] ?? 0))
                              }
                            >
                              <Plus size={16} />
                            </Button>
                          </div>
                        </td>
                        <td className="text-center">
                          {item.quantity} {item.productUnit || "unités"}
                          {item.alreadyDelivered > 0 && (
                            <div>
                              <small className="text-muted">
                                Reste à livrer : {item.remaining}
                              </small>
                            </div>
                          )}
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={item.notes}
                            onChange={(e) =>
                              handleItemNoteChange(index, e.target.value)
                            }
                            placeholder="Notes sur ce produit"
                            disabled={loading}
                          />
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeItem(index)}
                            disabled={loading}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => onCancel()}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  loading ||
                  !selectedOrder ||
                  orderLoading ||
                  stockErrors.length > 0 // Désactiver si erreurs de stock
                }
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Création en cours...
                  </>
                ) : (
                  <>
                    <Save size={16} className="me-1" />
                    Créer le bon de livraison
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CreateDeliveryNote;